import React, { lazy, Suspense, useState } from 'react';
import { Route, Routes, Navigate, useParams, useNavigate } from 'react-router-dom';
import { getAccessToken, loginWithCredentials, registerWithCredentials } from './auth.js';

/*
 * Code-split boundaries:
 *
 * 1. Public booking (/@handle) — lightweight, no auth, loads only booking code
 * 2. Dashboard (/dashboard/*) — authenticated, loads full app (schedule, rooms, settings)
 * 3. Landing (/) — minimal, just login CTA
 *
 * Heavy pages (Lobby, Room, Invite, Settings, Governance) are lazy-loaded
 * under /dashboard so the public routes stay lightweight.
 */

const ManageBookingPage = lazy(() => import('./plugins/booking/ManageBookingPage.jsx'));
const BookingRoutes = lazy(() => import('./plugins/booking/index.jsx'));
const BookingConfirmationPage = lazy(() => import('./plugins/booking/BookingConfirmationPage.jsx'));
const DashboardRoutes = lazy(() => import('./DashboardRoutes.jsx'));

const LoadingSpinner = () => (
  <div className="flex h-screen items-center justify-center text-text-secondary">Loading...</div>
);

/** Redirect /book/:handle → /@handle (backwards compat) */
function BookHandleRedirect() {
  const { handle } = useParams();
  const cleanHandle = handle.startsWith('@') ? handle : `@${handle}`;
  return <Navigate to={`/${cleanHandle}`} replace />;
}

/** Enforce that the handle starts with @ for booking routes */
function HandleRoute({ children }) {
  const { handle } = useParams();
  if (handle && handle.startsWith('@')) {
    return <>{children}</>;
  }
  return <Navigate to="/" replace />;
}

const PILLARS = [
  { n: '01', t: 'Co-working', d: 'Pomodoro ship-it sessions, matched by the shape of your task.' },
  { n: '02', t: 'Rhythmic scheduling', d: 'Broadcast Slow, Movement or Cognitive modes. Opt-out friendly.' },
  { n: '03', t: 'Community curation', d: 'Subscribe to local, authoritative streams of real events.' },
  { n: '04', t: 'A feed that ends', d: 'Awareness and action — never an infinite engagement loop.' },
];

/** Manifesto / Coming Soon page */
function Manifesto() {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen flex-col items-center bg-background px-6 py-24 md:px-20">
      <div className="max-w-2xl text-left">
        <h1 className="font-serif-display text-4xl font-semibold text-text-primary md:text-5xl">
          cycles<span style={{ color: 'var(--gold)' }}>.</span>social
        </h1>
        <p className="mono-label mt-4 tracking-widest" style={{ color: 'var(--gold)' }}>COMING SOON</p>
        
        <div className="mt-16 space-y-8 text-[18px] leading-relaxed text-text-secondary max-w-xl font-medium">
          <p>
            The digital grid runs too fast for the earth to heal and too rigid for humans to think. We have traded the sun's circadian arcs and the moon's seasonal harvests for an unimaginative standard—a modern crisis of desynchronization that leaves us alienated from our bodies and our ecology.
          </p>
          <p>
            Time is not a metric; it is a canvas. We are building a cooperative infrastructure to return timekeeping to the makers. By designing experimental devices of resonance, we are empowering communities to distribute new rhythms—bringing our relationships back into sync.
          </p>
        </div>
        
      </div>
    </div>
  );
}

/** Login page */
function Login() {
  const authed = Boolean(getAccessToken());
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('login'); // 'login' | 'register'

  if (authed) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'register') {
        await registerWithCredentials(email, password);
      } else {
        await loginWithCredentials(email, password);
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { background: 'var(--surface-raised)', borderColor: 'var(--color-border)' };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      {/* ── Hero / editorial column ── */}
      <section
        className="relative flex flex-col justify-between overflow-hidden border-b px-7 py-10 md:border-b-0 md:border-r md:px-12 md:py-14"
        style={{ borderColor: 'var(--color-border)', background: 'var(--color-surface)' }}
      >
        {/* concentric "cycles" motif */}
        <svg
          aria-hidden
          viewBox="0 0 400 400"
          className="pointer-events-none absolute -right-24 -top-24 h-[420px] w-[420px] opacity-50"
        >
          {[60, 110, 160].map((r) => (
            <circle key={r} cx="200" cy="200" r={r} fill="none" stroke="var(--color-border)" strokeWidth="1" />
          ))}
          <circle cx="200" cy="200" r="190" fill="none" stroke="var(--gold)" strokeWidth="1" strokeDasharray="2 8" opacity="0.4" />
          <g className="orbit">
            <circle cx="200" cy="10" r="4" fill="var(--gold)" />
            <circle cx="200" cy="390" r="2.5" fill="var(--terracotta)" />
          </g>
        </svg>

        <div className="rise relative" style={{ animationDelay: '0.02s' }}>
          <span className="font-serif-display text-[1.6rem] font-semibold text-text-primary">
            cycles<span style={{ color: 'var(--gold)' }}>.</span>social
          </span>
          <p className="mono-label mt-2">Member-owned · Built in Devon</p>
        </div>

        <div className="relative my-10 max-w-md md:my-0">
          <h1 className="rise font-serif-display text-[2.7rem] font-semibold leading-[1.04] text-text-primary md:text-[3.4rem]" style={{ animationDelay: '0.08s' }}>
            Work in rhythm,<br />
            <span style={{ color: 'var(--gold)' }}>together.</span>
          </h1>
          <p className="rise mt-5 max-w-sm text-[15px] leading-relaxed text-text-secondary" style={{ animationDelay: '0.16s' }}>
            cycles.social aligns focus sessions, shared scheduling and community
            events with your personal rhythms — and the natural cycles around you.
            A calmer way to do cooperative work.
          </p>

          <ul className="mt-9 flex flex-col">
            {PILLARS.map((p, i) => (
              <li
                key={p.n}
                className="rise flex items-baseline gap-4 border-t py-3"
                style={{ borderColor: 'var(--color-border)', animationDelay: `${0.22 + i * 0.06}s` }}
              >
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--gold)', fontSize: '0.7rem' }}>{p.n}</span>
                <span className="min-w-0">
                  <span className="text-[13.5px] font-semibold text-text-primary">{p.t}</span>
                  <span className="ml-2 text-[13px] text-text-secondary/80">{p.d}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="mono-label relative mt-10 md:mt-0">No cookies. No analytics. A tool you can put down.</p>
      </section>

      {/* ── Auth column ── */}
      <section className="flex items-center justify-center px-7 py-12 md:px-12">
        <div className="rise w-full max-w-sm" style={{ animationDelay: '0.12s' }}>
          <p className="mono-label">{mode === 'login' ? 'Returning member' : 'Founding cohort'}</p>
          <h2 className="mt-2 font-serif-display text-[2rem] font-semibold text-text-primary">
            {mode === 'login' ? 'Welcome back' : 'Create your account'}
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            {mode === 'login' ? 'Sign in to your workspace.' : 'Join the member-owned cooperative.'}
          </p>

          <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4">
            {error && (
              <div className="border p-3 text-sm text-red-400" style={{ borderColor: 'rgba(248,113,113,0.3)', background: 'rgba(248,113,113,0.08)' }}>
                {error}
              </div>
            )}
            <label className="block">
              <span className="mono-label">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full border p-3 text-sm text-text-primary placeholder-text-secondary/40 focus:outline-none"
                style={inputStyle}
                onFocus={(e) => { e.target.style.borderColor = 'var(--gold)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'var(--color-border)'; }}
                placeholder="you@example.com"
              />
            </label>
            <label className="block">
              <span className="mono-label">Password</span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full border p-3 text-sm text-text-primary placeholder-text-secondary/40 focus:outline-none"
                style={inputStyle}
                onFocus={(e) => { e.target.style.borderColor = 'var(--gold)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'var(--color-border)'; }}
                placeholder="••••••••"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="mt-1 w-full py-3 text-sm font-semibold text-white transition hover:-translate-y-[1px] hover:opacity-95 disabled:opacity-50"
              style={{ background: 'var(--terracotta)' }}
            >
              {loading ? '…' : mode === 'register' ? 'Create account' : 'Sign in'}
            </button>
          </form>

          <div className="rule-gold my-6" />

          <p className="text-center text-xs text-text-secondary">
            {mode === 'login' ? (
              <>New here?{' '}
                <button type="button" onClick={() => { setMode('register'); setError(''); }} className="font-semibold underline-offset-2 hover:underline" style={{ color: 'var(--gold)' }}>
                  Request a place in the cohort
                </button>
              </>
            ) : (
              <>Already a member?{' '}
                <button type="button" onClick={() => { setMode('login'); setError(''); }} className="font-semibold underline-offset-2 hover:underline" style={{ color: 'var(--gold)' }}>
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>
      </section>
    </div>
  );
}

function TimeAppRedirect() {
  React.useEffect(() => {
    if (window.location.pathname === '/time' || window.location.pathname === '/time/') {
      window.location.replace('/time/index.html');
    }
  }, []);
  return null;
}

export default function App() {
  return (
    <Routes>
      {/* Public booking — /:handle (guest, no auth, lightweight) */}
      <Route
        path="/:handle"
        element={
          <HandleRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <BookingRoutes />
            </Suspense>
          </HandleRoute>
        }
      />
      <Route
        path="/:handle/t/:typeSlug"
        element={
          <HandleRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <BookingRoutes />
            </Suspense>
          </HandleRoute>
        }
      />
      <Route
        path="/embed/:handle/t/:typeSlug"
        element={
          <HandleRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <BookingRoutes />
            </Suspense>
          </HandleRoute>
        }
      />
      <Route
        path="/:handle/confirmation/:slug"
        element={
          <HandleRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <BookingRoutes />
            </Suspense>
          </HandleRoute>
        }
      />

      {/* Stable confirmation URL (bookmarkable by guests) */}
      <Route
        path="/book/confirmation/:slug"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <ManageBookingPage />
          </Suspense>
        }
      />
      
      {/* Manage link URL */}
      <Route
        path="/booking/:slug"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <ManageBookingPage />
          </Suspense>
        }
      />

      {/* Backwards compat: /book/:handle → /@:handle */}
      <Route path="/book/:handle" element={<BookHandleRedirect />} />

      {/* Dashboard — authenticated, code-split, loads full app */}
      <Route
        path="/dashboard/*"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <DashboardRoutes />
          </Suspense>
        }
      />

      {/* Login page */}
      <Route path="/login" element={<Login />} />

      {/* Landing — Coming Soon Manifesto */}
      <Route path="/" element={<Manifesto />} />

      {/* Redirect /time without trailing slash to /time/ to escape React Router */}
      <Route path="/time" element={<TimeAppRedirect />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
